import { useEffect, useRef } from 'react'
import { Icon } from './Icon'
import { Markdown } from './markdown/Markdown'

/**
 * Окно чтения правового документа.
 *
 * Документ открывается внутри приложения, а не по внешней ссылке:
 * внешний домен живёт своей жизнью, и пользователь не должен
 * соглашаться со страницей 404. Текст приходит из настроек владельца.
 *
 * Компонент презентационный: загрузку делает вызывающая сторона
 * (LegalConsent или страница «Ещё»), сюда приходит готовое состояние.
 * Пакет ui не ходит в сеть — экран входа рендерится в тестах без мока.
 *
 * На десктопе — центрированное окно, на телефоне — почти во весь
 * экран: длинный юридический текст в маленьком окошке нечитаем.
 */
export function LegalDocumentDialog({ open, title, text, loading, error, onClose, onRetry }) {
  const closeRef = useRef(null)

  // Фокус на закрытие при открытии: диалог открыт кнопкой, Esc и Tab
  // должны работать сразу, без клика внутрь.
  useEffect(() => {
    if (open) closeRef.current?.focus()
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="legal-doc__overlay" onClick={onClose} role="presentation">
      <div
        className="legal-doc"
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Документ'}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="legal-doc__head">
          <h2 className="legal-doc__title">{title || 'Документ'}</h2>
          <button
            ref={closeRef}
            type="button"
            className="legal-doc__close"
            onClick={onClose}
            aria-label="Закрыть"
          >
            <Icon name="close" size={18} />
          </button>
        </header>

        <div className="legal-doc__body">
          {loading && (
            <div className="legal-doc__state">
              <span className="spinner" />
            </div>
          )}

          {!loading && error && (
            <div className="legal-doc__state">
              <p>{error}</p>
              {onRetry && (
                <button type="button" className="btn btn--secondary" onClick={onRetry}>
                  Повторить
                </button>
              )}
            </div>
          )}

          {!loading && !error && <Markdown text={text} />}
        </div>
      </div>
    </div>
  )
}
